// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import ztable from 'ztable';

import PageHeader from '../../../PageHeader.jsx';
import ContentBox from '../../../ContentBox.jsx';

import ChildMeasurements from './ChildMeasurements.jsx';
import ChildLengthAgeChart from './ChildLengthAgeChart.jsx';
import ChildWeightAgeChart from './ChildWeightAgeChart.jsx';
import ChildWeightLengthChart from './ChildWeightLengthChart.jsx';

import ageWeight from './constants/wtage';
import ageLength from './constants/statage';
import lengthWeight from './constants/wtstat';

type Props = {
  dob: string,
  sex: string,
  childMeasurements: Object[],
  wellChildChecks: Object[],
}

class ChildPageComponent extends Component<Props> {
  // returns age in months
  getAge(dateString: string) {
    const dob = moment(this.props.dob);
    const date = moment(dateString);
    return date.diff(dob, 'months');
  }

  // returns age string with __ y __ m
  getAgeString(dateString: string) {
    const dob = moment(this.props.dob);
    const date = moment(dateString);
    const years = date.diff(dob, 'years');
    dob.add(years, 'years');
    const months = date.diff(dob, 'months');
    return `${years} y, ${months} m`;
  }

  getPercentile(value: number, l: number, m: number, s: number) {
    if (!value || !l || !m || !s) {
      return null;
    }
    let zscore;
    if (l === 0) {
      zscore = Math.log(value / m) / s;
    } else {
      zscore = (((value / m) ** l) - 1) / (l * s);
    }
    const percentile = Math.round(ztable(zscore) * 100);
    return percentile;
  }

  render() {
    // key to index into js constants files for percentiles, default to male
    const sex = this.props.sex === 'F' ? 'female' : 'male';

    // add measurements from WCCs 2 Years old onwards if measurements exist
    let measurements = this.props.childMeasurements.slice();
    for (let i = 0; i < this.props.wellChildChecks.length; i++) {
      const {
        date, notes, vitals, editors, editDates,
      } = this.props.wellChildChecks[i];
      const { weight, length } = vitals;
      if (weight || length) {
        measurements.push({
          date,
          weight,
          length,
          notes,
          editors,
          editDates,
          showDelete: false,
          showEdit: false,
        });
      }
    }

    measurements = measurements.map((item) => {
      const age = this.getAge(item.date);
      // constrain indices to constants array lengths
      const ageIndex = Math.max(0, Math.min(217, age - 23));
      const weightL = ageWeight[sex].l[ageIndex];
      const weightM = ageWeight[sex].m[ageIndex];
      const weightS = ageWeight[sex].s[ageIndex];
      const lengthL = ageLength[sex].l[ageIndex];
      const lengthM = ageLength[sex].m[ageIndex];
      const lengthS = ageLength[sex].s[ageIndex];

      // Rounded to nearest 0.5 (e.g. 45.5, 46.5, etc), dummy length if DNE
      const roundedLength = item.length ? Math.floor(item.length) + 0.5 : 77;
      const maxIndex = lengthWeight[sex].l.length - 1;
      const lengthIndex = roundedLength === 77 ? 0 : Math.min(Math.max(roundedLength - 76.5, 0), maxIndex);
      const lwL = lengthWeight[sex].l[lengthIndex];
      const lwM = lengthWeight[sex].m[lengthIndex];
      const lwS = lengthWeight[sex].s[lengthIndex];

      return {
        age,
        ageString: this.getAgeString(item.date),
        weightPercentile: this.getPercentile(item.weight, weightL, weightM, weightS),
        lengthPercentile: this.getPercentile(item.length, lengthL, lengthM, lengthS),
        lwPercentile: this.getPercentile(item.weight, lwL, lwM, lwS),
        ...item,
      };
    });

    measurements.sort((a, b) => {
      if (!a.date) {
        return 1;
      }
      if (!b.date) {
        return -1;
      }
      if (a.date < b.date) {
        return 1;
      }
      if (b.date < a.date) {
        return -1;
      }
      return 0;
    });

    return (
      <div>
        <PageHeader title='Growth Charts (2-20 Years)' />
        <ContentBox title='Measurements' id='measurements'>
          <ChildMeasurements measurements={measurements} />
        </ContentBox>
        <ContentBox title='Weight-for-Age' id='weightAge' collapsable>
          <ChildWeightAgeChart measurements={measurements} sex={sex} />
        </ContentBox>
        <ContentBox title='Height-for-Age' id='heightAge' collapsable>
          <ChildLengthAgeChart measurements={measurements} sex={sex} />
        </ContentBox>
        <ContentBox title='Weight-for-Height' id='weightHeight' collapsable>
          CDC data stops at 124cm for weight-for-height, so only data that fits is shown.
          <ChildWeightLengthChart measurements={measurements} sex={sex} />
        </ContentBox>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  dob: state.patient.data.generalInfo.dob,
  sex: state.patient.data.generalInfo.sex,
  childMeasurements: state.patient.data.childMeasurements,
  wellChildChecks: state.patient.data.wellChildChecks.slice(7),
});

const ChildPage = connect(
  mapStateToProps,
)(ChildPageComponent);

export default ChildPage;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/growthChart/child/ChildPage.jsx